/**
 * Serpentine layout for the snake timeline.
 *
 * Extracted from the component so the geometry can be tested without a DOM.
 *
 * The important decision here is **fixed node spacing**. The first version
 * divided the container width by the column count, so three nodes on a 1600px
 * screen sat 800px apart with nothing between them — technically a snake, in
 * practice a maze. Spacing is now constant and the column count is derived from
 * it, so a wide screen shows *more nodes per row* rather than the same nodes
 * further apart. Density stays readable at any width and at any event count.
 */

export interface LayoutPoint<T> {
  x: number;
  y: number;
  row: number;
  col: number;
  /** True when this node sits at the end of a row and the path turns here. */
  isTurn: boolean;
  event: T;
}

export interface LayoutSegment<T> {
  d: string;
  from: LayoutPoint<T>;
  to: LayoutPoint<T>;
  isArc: boolean;
  sweep: number;
  radius: number;
  gapDays: number;
  /** Set when the calendar year changes across this segment. */
  yearBoundary: number | null;
  midX: number;
  midY: number;
}

export interface Layout<T> {
  points: LayoutPoint<T>[];
  segments: LayoutSegment<T>[];
  height: number;
  columns: number;
  spacing: number;
}

/** Horizontal distance between node centres. Wide enough for a compact label. */
export const NODE_SPACING = 210;
/** Half the width a node's label may occupy. */
export const LABEL_HALF_WIDTH = 96;
export const PAD_X = 104;
export const PAD_Y = 74;
export const ROW_HEIGHT = 172;
/** One column is degenerate; two is the narrowest useful serpentine. */
export const MIN_COLUMNS = 2;
export const MAX_COLUMNS = 8;

export function columnsFor(width: number): number {
  const usable = Math.max(width - PAD_X * 2, NODE_SPACING);
  return Math.max(MIN_COLUMNS,
    Math.min(MAX_COLUMNS, Math.floor(usable / NODE_SPACING) + 1));
}

export function dayGap(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);
}

export function buildLayout<T extends { timestamp: string }>(
  events: T[],
  width: number,
): Layout<T> {
  const columns = columnsFor(width);
  const spacing = columns > 1
    ? Math.min(NODE_SPACING, (width - PAD_X * 2) / (columns - 1))
    : 0;
  const rows = Math.max(1, Math.ceil(events.length / columns));

  const points: LayoutPoint<T>[] = events.map((event, index) => {
    const row = Math.floor(index / columns);
    const col = index % columns;
    // Boustrophedon: odd rows run backwards, so the eye follows the sequence
    // without needing arrowheads.
    const effectiveCol = row % 2 === 0 ? col : columns - 1 - col;
    const lastInRow = col === columns - 1;
    return {
      x: PAD_X + effectiveCol * spacing,
      y: PAD_Y + row * ROW_HEIGHT,
      row,
      col,
      isTurn: lastInRow && index !== events.length - 1,
      event,
    };
  });

  const segments: LayoutSegment<T>[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i]!;
    const to = points[i + 1]!;
    const gapDays = dayGap(from.event.timestamp, to.event.timestamp);
    const fromYear = new Date(from.event.timestamp).getFullYear();
    const toYear = new Date(to.event.timestamp).getFullYear();
    const yearBoundary = fromYear !== toYear ? toYear : null;

    if (from.row === to.row) {
      segments.push({
        d: `M${from.x} ${from.y} H${to.x}`,
        from, to, isArc: false, sweep: 0, radius: 0, gapDays, yearBoundary,
        midX: (from.x + to.x) / 2, midY: from.y,
      });
    } else {
      // A true semicircle, with the sweep flipped by row parity so each lobe
      // bulges outward rather than doubling back over the path.
      const radius = Math.abs(to.y - from.y) / 2;
      const sweep = from.row % 2 === 0 ? 1 : 0;
      segments.push({
        d: `M${from.x} ${from.y} A${radius} ${radius} 0 0 ${sweep} ${to.x} ${to.y}`,
        from, to, isArc: true, sweep, radius, gapDays, yearBoundary,
        midX: from.x + (sweep ? radius : -radius),
        midY: (from.y + to.y) / 2,
      });
    }
  }

  return {
    points,
    segments,
    height: PAD_Y * 2 + (rows - 1) * ROW_HEIGHT,
    columns,
    spacing,
  };
}
